import _ from 'underscore';
import Mui from 'materials';
import { FormattedMessage, useIntl } from 'react-intl';
import { FC, useMemo, useState } from 'react';
import { Box, Palette, useTheme } from '@mui/material';
import { Ban, CheckCircle, Search } from 'lucide-react';

import Paper from 'components/paper';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import Pagination from 'components/pagination';
import ListRow from 'components/list/list-row';
import ListCell from 'components/list/list-cell';
import List, { HeadCell } from 'components/list/list';
import FilterSidebar from 'components/filter-sidebar';
import CreateFeeder from 'views/self-service/features/feeders/components/create-feeder';
import UpdateFeeder from 'views/self-service/features/feeders/components/update-feeder';

import Breadcrumbs from 'components/breadcrumbs';
import SelfServiceTabsHeader from '../../components/self-service-tabs-header';

import { usePagination } from 'hooks/use-pagination';
import { loadState, saveState } from 'utils/storage';
import { Feeder } from 'views/self-service/features/feeders/types/feeders.type';
import { FeedersProvider, useFeedersState } from './context/feeders-context';

type FeedersProps = {};

const getIcon = (status: boolean, color: Palette) => {
  const Icon = status ? Ban : CheckCircle;
  return <Icon color={status ? color.darkBlue.o25 : color.bluViu.main} size={20} strokeWidth={3} />;
};

type Filters = {
  searchValue: string;
  showActiveFeeders: boolean;
  showInactiveFeeders: boolean;
};

const defaultValues: Filters = {
  searchValue: '',
  showActiveFeeders: true,
  showInactiveFeeders: false,
};

const PAGINATION_LIMIT = 7;
const PAGE_NAME_ID = 'selfService.feeders';

const Feeders: FC<FeedersProps> = ({}) => {
  const intl = useIntl();
  const theme = useTheme();
  const { isLoading, feeders, feederBrands } = useFeedersState();

  // #region STATE
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);
  const [filters, setFilters] = useState<Filters>(loadState({ key: 'feederFilters' }) || defaultValues);
  // #endregion STATE

  // #region OPTIONS
  const feedersListOptions: HeadCell[] = [
    { id: 'name', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.name` }) },
    { id: 'brand', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.brand` }) },
    { id: 'storageCapacity', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.storageCapacity` }) },
    { id: 'supportedBiomass', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.supportedBiomass` }) },
    { id: 'hidden', align: 'center', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.active` }) },
    {
      id: 'actions',
      label: '',
      autoWidth: true,
    },
  ];
  // #endregion OPTIONS

  // #region HANDLERS
  const resetStates = () => updateFilters(defaultValues);

  const updateFilters = (newFilters: Filters) => {
    setFilters(newFilters);
    saveState({ key: 'feederFilters', state: newFilters });
  };
  // #endregion HANDLRES

  // #region RENDERS
  const feedersList = useMemo(() => {
    let rows: Feeder[] | undefined = feeders;
    rows = _.chain(feeders)
      .filter((feeder) => (filters.showActiveFeeders ? true : feeder.hidden))
      .filter((feeder) => (filters.showInactiveFeeders ? true : !feeder.hidden))
      .filter((feeder) => feeder.name.toLowerCase().includes(filters.searchValue.toLowerCase()))
      .sortBy((feeder) => feeder.name.toLowerCase())
      .value();

    return rows;
  }, [filters, feeders]);
  // #endregion RENDERS

  return (
    <>
      <SelfServiceTabsHeader utils={<Breadcrumbs />} />
      <Box display={'flex'} justifyContent={'flex-end'}>
        <CreateFeeder />
      </Box>
      <Paper transparent>
        <FilterSidebar
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          showClearFilter={!!filters.searchValue || !filters.showActiveFeeders || filters.showInactiveFeeders}
          clearFilters={() => {
            resetStates();
          }}>
          <Box display={'flex'} flexDirection={'row'} gap={4}>
            <Switch
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showActive` })}
              value={filters.showActiveFeeders}
              onChange={() => {
                handlePagination(1);
                let newFilters = { ...filters, showActiveFeeders: !filters.showActiveFeeders };
                updateFilters(newFilters);
              }}
            />
            <Switch
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showInactive` })}
              value={filters.showInactiveFeeders}
              onChange={() => {
                handlePagination(1);
                let newFilters = { ...filters, showInactiveFeeders: !filters.showInactiveFeeders };
                updateFilters(newFilters);
              }}
            />
          </Box>
          <TextField
            value={filters.searchValue}
            label={intl.formatMessage({ id: 'general.search' })}
            onChange={(event) => {
              handlePagination(1);
              let newFilters = { ...filters, searchValue: event.target.value as string };

              updateFilters(newFilters);
            }}
            endAdornment={<Search size={18} />}
          />
        </FilterSidebar>
        <List
          isLoading={isLoading}
          noResult={{
            show: !feedersList.length,
            text: intl.formatMessage({ id: 'general.noResults' }),
          }}
          options={feedersListOptions}>
          {feedersList.slice(from, to).map((feeder, idx) => (
            <ListRow key={feeder.id}>
              <ListCell align="left">{feeder.name}</ListCell>
              <ListCell align="left" capitalize>
                {feederBrands?.map((brand) => (brand.id === feeder.feederBrandId ? brand.name : ''))}
              </ListCell>
              <ListCell align="left">{feeder.storageCapacity} kg</ListCell>
              <ListCell align="left">
                {feeder.feedingCapacity} <FormattedMessage id={`${PAGE_NAME_ID}.list.kgFeeder`} />
              </ListCell>
              <ListCell align="center">
                {getIcon(feeder.hidden, theme.palette)}
              </ListCell>
              <ListCell>
                <UpdateFeeder idx={idx} feeder={feeder} />
              </ListCell>
            </ListRow>
          ))}
        </List>
        {feedersList.length > PAGINATION_LIMIT && (
          <Pagination currentPage={currentPage} pages={Math.ceil(feedersList.length / PAGINATION_LIMIT)} onClick={handlePagination} />
        )}
      </Paper>
    </>
  );
};

const FeedersContainer: React.FC<{}> = ({}) => {
  return (
    <FeedersProvider>
      <Feeders />
    </FeedersProvider>
  );
};

export default FeedersContainer;
